import { useState } from 'react';
import { useRouter } from 'next/router';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  Avatar,
  Badge,
  ConfirmButton,
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  EmptyIndicator,
  ProgressBar
} from 'components/common';
import { Box } from 'components/common/box';
import { Icon } from 'components/common/icons';
import { CheckboxInput } from 'components/common/form/common';

import type { GetCompetencyMappingRes } from 'shared-types/responses';

const ProfileCompetencyMappingResult = () => {
  const { messages, theme } = useReduxState();

  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<number[]>(
    []
  );

  const { query } = useRouter();

  const { data, loading, updateData } = useApiData<GetCompetencyMappingRes>({
    url: `/admin/competency-mapping/${query.competencyMappingId}`
  });

  const mapping = data?.mapping;

  const handleSelect = (suggestionId: number, selected: boolean) => {
    let selectedIds = [...selectedSuggestionIds];
    if (selected) selectedIds = [...selectedIds, suggestionId];
    else selectedIds = selectedIds.filter((id) => id !== suggestionId);

    setSelectedSuggestionIds(selectedIds);
  };

  const handleSubmit = async () => {
    await api.put(`/admin/competency-mapping/${mapping?.id}/confirm`, {
      acceptedSuggestionIds: selectedSuggestionIds
    });

    await updateData();

    return true;
  };

  return (
    <div className="mapping">
      <Box loading={loading} padding={0} title={messages.competencyMapping}>
        {mapping ? (
          <>
            <div className="inner">
              <div className="mapping-info">
                <ContentLabel horizontal={true} label={messages.commonStatus}>
                  <ContentValue>
                    {mapping.status === 'analyzed' ? (
                      <Badge
                        category="warning-light"
                        text={messages.competencyMappingAnalyzed}
                      />
                    ) : mapping.status === 'completed' ? (
                      <Badge
                        category="success-light"
                        text={messages.commonCompleted}
                      />
                    ) : null}
                  </ContentValue>
                </ContentLabel>

                {mapping.completedAt ? (
                  <ContentLabel
                    horizontal={true}
                    label={messages.commonCompleted}
                  >
                    <ContentValue>
                      <DisplayFormattedDate date={mapping.completedAt} />
                    </ContentValue>
                  </ContentLabel>
                ) : null}
              </div>

              <div className="suggestions">
                <h3 className="suggestions-heading">
                  {messages.competencyMappingSuggestedCompetencies}

                  {` (${mapping.suggestions?.length || 0})`}
                </h3>

                {mapping.suggestions?.length ? (
                  <div className="suggestion-items">
                    {mapping.suggestions.map((suggestion) => (
                      <div className="suggestion" key={suggestion.id}>
                        <div className="suggestion-header">
                          <div className="suggestion-icon">
                            <Avatar
                              backgroundColor={theme.successLightColor}
                              color={theme.successLightTextColor}
                              fontSize="1.3rem"
                              icon="medal"
                              padding="8px"
                              size="32px"
                            />
                          </div>
                          <div className="suggestion-title">
                            <div className="suggestion-title-text">
                              {suggestion.title}
                            </div>

                            {suggestion.level ? (
                              <div className="suggestion-level">
                                {`${messages.commonLevel}: ${suggestion.level}`}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="suggestion-info">
                          <ContentLabel
                            horizontal={true}
                            label={messages.competencyMappingConfidence}
                            spacing={10}
                          >
                            <ContentValue>
                              <ProgressBar
                                color={theme.chartMainColor}
                                horizontal={true}
                                label={`${suggestion.confidence}%`}
                                progress={suggestion.confidence / 100}
                                style={{ width: 150 }}
                              />
                            </ContentValue>
                          </ContentLabel>

                          <ContentLabel
                            horizontal={true}
                            label={messages.competencyMappingJustification}
                            spacing={10}
                          >
                            <ContentValue>
                              {suggestion.justification}
                            </ContentValue>
                          </ContentLabel>
                        </div>

                        <div className="suggestion-status">
                          {mapping.status === 'analyzed' ? (
                            <div className="suggestion-status-accept">
                              <CheckboxInput
                                checked={selectedSuggestionIds.includes(
                                  suggestion.id
                                )}
                                id={`suggestion-status-accept-${suggestion.id}`}
                                label={
                                  <span>
                                    {messages.commonAccept}

                                    {suggestion.matchedExisting ? null : (
                                      <span
                                        className="suggestion-status-accept-indicator"
                                        style={{
                                          display: 'inline-block',
                                          fontSize: '0.6rem',
                                          marginTop: 1,
                                          padding: '0 2px',
                                          verticalAlign: 'top'
                                        }}
                                      >
                                        <Icon icon="asterisk" />

                                        <span className="sr-only">
                                          {
                                            messages.competencyMappingAcceptNewCompetencyInfo
                                          }
                                        </span>
                                      </span>
                                    )}
                                  </span>
                                }
                                labelTooltip={
                                  suggestion.matchedExisting
                                    ? undefined
                                    : messages.competencyMappingAcceptNewCompetencyInfo
                                }
                                labelTooltipShowIcon={false}
                                onChange={(ev) =>
                                  handleSelect(
                                    suggestion.id,
                                    (ev.target as HTMLInputElement).checked
                                  )
                                }
                              />
                            </div>
                          ) : mapping.status === 'completed' ? (
                            <div className="suggestion-status-badge">
                              {suggestion.acceptedAt ? (
                                <Badge
                                  category="success-light"
                                  text={messages.commonAccepted}
                                />
                              ) : (
                                <Badge
                                  category="error-light"
                                  text={messages.commonDiscarded}
                                />
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty">
                    <EmptyIndicator
                      icon="list"
                      size="small"
                      title={messages.competencyMappingNoSuggestions}
                    />
                  </div>
                )}
              </div>
            </div>

            {mapping.status === 'analyzed' ? (
              <div className="footer">
                <ConfirmButton
                  actionText={messages.commonAccept}
                  buttonText={getMessageWithPlaceholders({
                    message: messages.competencyMappingAcceptCompetencies,
                    placeholders: { number: selectedSuggestionIds.length }
                  })}
                  buttonTooltip={
                    !selectedSuggestionIds.length
                      ? messages.competencyMappingAcceptNotSelected
                      : undefined
                  }
                  category="success"
                  confirmText={messages.competencyMappingAcceptConfirm}
                  confirmTitle={messages.competencyMapping}
                  disabled={!selectedSuggestionIds.length}
                  onConfirm={handleSubmit}
                  successMessage={messages.competencyMappingAcceptSuccess}
                />

                <ConfirmButton
                  actionCategory="danger"
                  actionText={messages.commonDiscard}
                  buttonText={messages.competencyMappingDiscard}
                  category="light"
                  confirmText={messages.competencyMappingDiscardConfirm}
                  confirmTitle={messages.competencyMapping}
                  disabled={Boolean(selectedSuggestionIds.length)}
                  onConfirm={handleSubmit}
                  successMessage={messages.competencyMappingDiscardSuccess}
                />
              </div>
            ) : null}
          </>
        ) : (
          <EmptyIndicator
            icon="wand-magic-sparkles"
            title={messages.competencyMappingNotFound}
          />
        )}
      </Box>

      <style jsx>{`
        .inner {
          padding: 15px;
        }
        .suggestions {
          margin-top: 30px;
        }
        .suggestions-heading {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          margin: 0 0 15px;
        }
        .suggestion-items {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .suggestion {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          position: relative;
        }
        .suggestion-header {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .suggestion-icon {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .suggestion-title {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .suggestion-title-text {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 700;
        }
        .suggestion-status {
          display: inline-block;
          flex-grow: 0;
          flex-shrink: 0;
          margin-top: 15px;
        }
        .suggestion-status-accept {
          background-color: ${theme.lightGreyDark};
          border-radius: ${theme.buttonBorderRadius};
          padding: 3px 6px;
        }
        .suggestion-status-accept-indicator {
          display: inline-block;
          font-size: 0.6rem;
          margin-top: 1;
          padding: 0 2px;
          vertical-align: top;
        }
        .suggestion-level {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 2px;
        }
        .suggestion-info {
          margin-top: 15px;
        }
        .footer {
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: flex-end;
          padding: 15px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .suggestion-header {
            padding-right: 150px;
          }
          .suggestion-status {
            margin-top: 0;
            position: absolute;
            top: 20px;
            right: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCompetencyMappingResult;
